import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryPersistReady } from '../../../../../providers/queryPersistReady';
import { useAuthUser } from '../../../../../providers/AuthProvider';
import { useUserDevices } from '../../../../../hooks/useUserDevices';
import { spectralWorkspaceStore } from '../../../store/spectralWorkspaceStore';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSnapshot,
  TrendSignalKey,
} from '../types';
import {
  readCachedAnalysisSnapshot,
  writeCachedAnalysisSnapshot,
} from '../utils/analysisSnapshotCache';
import { hasPendingSessionEventParse } from '../utils/sessionLoadingState';
import { buildHydrateEntryFromConfiguration } from '../utils/buildHydrateEntryFromConfiguration';
import {
  buildSnapshotFromWorkspace,
  createDefaultWorkspaceSettings,
} from '../utils/buildWorkspaceSnapshot';
import { getCachedSessionConfiguration } from '../queries/prefetchSessionConfigurations';
import { buildSensorDeviceLookup } from '../utils/sessionDevice';
import { sessionGroupQueryOptions } from '../queries/useSessionGroupQuery';
import { sessionGroupsListQueryOptions } from '../queries/useSessionGroupsListQuery';
import {
  useWorkspaceSessions,
  type HydrateSessionEntry,
} from './useWorkspaceSessions';

const AUTOSAVE_DELAY_MS = 800;

const serializeSnapshotForSave = (snapshot: SpectralWorkspaceSnapshot) =>
  JSON.stringify({
    ...snapshot,
    updatedAt: undefined,
  });

type WorkspaceMeta = Pick<
  SpectralWorkspaceSnapshot,
  'analysisId' | 'name' | 'description' | 'author' | 'createdAt'
>;

type WorkspacePersistence = {
  hydrated: boolean;
  skipSave: boolean;
  lastSavedSnapshot: string | null;
  saveInFlight: boolean;
};

const buildHydrateKey = (groupId: string | null, sessionIds: number[]) =>
  groupId
    ? `${groupId}:${[...sessionIds].sort((a, b) => a - b).join(',')}`
    : '';

export const useSpectralWorkspace = (analysisId: string | undefined) => {
  const queryClient = useQueryClient();
  const queryPersistReady = useQueryPersistReady();
  const user = useAuthUser();
  const { devices: userDevices } = useUserDevices(user?.id?.toString());
  const sensorDeviceLookup = useMemo(
    () => buildSensorDeviceLookup(userDevices),
    [userDevices]
  );
  const {
    sessionList,
    readySessions,
    isLoading,
    hydrateSessions,
    parsePendingSessionEvents,
  } = useWorkspaceSessions(sensorDeviceLookup);
  const [meta, setMeta] = useState<WorkspaceMeta | null>(null);
  const [activeSessionGroupId, setActiveSessionGroupId] = useState<
    string | null
  >(null);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(() => {
    if (!analysisId) return false;
    return readCachedAnalysisSnapshot(analysisId) == null;
  });
  const [isPreparingSessions, setIsPreparingSessions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedFeature, setSelectedFeature] = useState<TrendSignalKey>('HR');
  const [showOverlaySessions, setShowOverlaySessions] = useState(false);
  const [selectedOverlaySessionIds, setSelectedOverlaySessionIds] = useState<
    string[]
  >([]);
  const [activeViewId, setActiveViewId] = useState<ChartViewId>('full');
  const [customSegments, setCustomSegments] = useState<CustomTimeSegment[]>([]);
  const [trendStart, setTrendStart] = useState<string | null>(null);
  const [trendEnd, setTrendEnd] = useState<string | null>(null);
  const [trendNormalizationEnabled, setTrendNormalizationEnabled] =
    useState(false);
  const [trendLowPassEnabled, setTrendLowPassEnabled] = useState(false);
  const [selectedSpectraSessionId, setSelectedSpectraSessionId] = useState<
    string | null
  >(null);
  const [spectraStart, setSpectraStart] = useState<string | null>(null);
  const [spectraEnd, setSpectraEnd] = useState<string | null>(null);
  const [spectraLogScale, setSpectraLogScale] = useState(false);

  const persistenceRef = useRef<WorkspacePersistence>({
    hydrated: false,
    skipSave: true,
    lastSavedSnapshot: null,
    saveInFlight: false,
  });
  const lastHydratedKeyRef = useRef<string | null>(null);

  const applyWorkspaceSettings = useCallback(
    (workspace: SpectralWorkspaceSnapshot['workspace']) => {
      setSelectedFeature(workspace.selectedFeature);
      setShowOverlaySessions(workspace.showOverlaySessions);
      setSelectedOverlaySessionIds(workspace.selectedOverlaySessionIds);
      setActiveViewId(workspace.activeViewId);
      setTrendStart(workspace.trendStart);
      setTrendEnd(workspace.trendEnd);
      setTrendNormalizationEnabled(workspace.trendNormalizationEnabled);
      setTrendLowPassEnabled(workspace.trendLowPassEnabled);
      setSelectedSpectraSessionId(workspace.selectedSpectraSessionId);
      setSpectraStart(workspace.spectraStart);
      setSpectraEnd(workspace.spectraEnd);
      setSpectraLogScale(workspace.spectraLogScale);
    },
    []
  );

  const applySnapshot = useCallback(
    (snapshot: SpectralWorkspaceSnapshot) => {
      setMeta({
        analysisId: snapshot.analysisId,
        name: snapshot.name,
        description: snapshot.description,
        author: snapshot.author,
        createdAt: snapshot.createdAt,
      });
      setCustomSegments(snapshot.customSegments);
      applyWorkspaceSettings(snapshot.workspace);
      setActiveSessionGroupId(snapshot.activeSessionGroupId ?? null);
      persistenceRef.current.lastSavedSnapshot =
        serializeSnapshotForSave(snapshot);
      persistenceRef.current.hydrated = true;
      persistenceRef.current.skipSave = false;
    },
    [applyWorkspaceSettings]
  );

  const loadSessionsForGroup = useCallback(
    async (
      groupId: string | null,
      options: {
        force?: boolean;
        deferFetch?: boolean;
        deferEventParse?: boolean;
      } = {}
    ) => {
      if (!groupId) {
        lastHydratedKeyRef.current = null;
        hydrateSessions([], { bulkLoad: false });
        return;
      }

      setIsPreparingSessions(true);

      try {
        const group = await queryClient.fetchQuery(
          sessionGroupQueryOptions(groupId)
        );
        if (!group) {
          lastHydratedKeyRef.current = null;
          hydrateSessions([], { bulkLoad: false });
          return;
        }

        const hydrateKey = buildHydrateKey(groupId, group.sessionIds);
        if (!options.force && lastHydratedKeyRef.current === hydrateKey) {
          return;
        }

        lastHydratedKeyRef.current = hydrateKey;

        const entries: HydrateSessionEntry[] = [];
        const uncachedSessionIds: number[] = [];

        group.sessionIds.forEach(sessionId => {
          const cached = getCachedSessionConfiguration(queryClient, sessionId);
          if (cached) {
            entries.push(
              buildHydrateEntryFromConfiguration(
                sessionId,
                cached,
                sensorDeviceLookup
              )
            );
            return;
          }

          uncachedSessionIds.push(sessionId);
          entries.push({
            sessionId,
            hasHrData: true,
            hasHrDataRepaired: false,
          });
        });

        entries.sort((left, right) => left.sessionId - right.sessionId);

        const needsFetch = uncachedSessionIds.length > 0;

        hydrateSessions(entries, {
          bulkLoad: needsFetch,
          // Start network promptly when we need payloads (toast tracks progress).
          deferFetch: needsFetch ? false : (options.deferFetch ?? true),
          deferEventParse: options.deferEventParse ?? true,
        });
      } catch {
        lastHydratedKeyRef.current = null;
        hydrateSessions([], { bulkLoad: false });
      } finally {
        setIsPreparingSessions(false);
      }
    },
    [hydrateSessions, queryClient, sensorDeviceLookup]
  );

  const ensureSessionEventsParsed = useCallback(() => {
    parsePendingSessionEvents();
  }, [parsePendingSessionEvents]);

  useEffect(() => {
    lastHydratedKeyRef.current = null;
  }, [analysisId]);

  useEffect(() => {
    if (!analysisId || !queryPersistReady) {
      if (!analysisId) {
        setIsLoadingAnalysis(false);
      }
      return;
    }

    let cancelled = false;
    persistenceRef.current.hydrated = false;
    persistenceRef.current.skipSave = true;
    setLoadError(null);

    const cachedSnapshot = readCachedAnalysisSnapshot(analysisId);
    if (cachedSnapshot) {
      applySnapshot(cachedSnapshot);
      setIsLoadingAnalysis(false);

      void loadSessionsForGroup(cachedSnapshot.activeSessionGroupId ?? null, {
        deferFetch: true,
        deferEventParse: true,
      });
    } else {
      setIsLoadingAnalysis(true);
    }

    spectralWorkspaceStore
      .getAnalysis(analysisId)
      .then(async snapshot => {
        if (cancelled) return;

        if (!snapshot) {
          setLoadError('Analisi non trovata.');
          return;
        }

        writeCachedAnalysisSnapshot(analysisId, snapshot);
        applySnapshot(snapshot);

        void queryClient.prefetchQuery(sessionGroupsListQueryOptions());

        void loadSessionsForGroup(snapshot.activeSessionGroupId ?? null, {
          deferFetch: true,
          deferEventParse: true,
        });
      })
      .catch(error => {
        if (cancelled) return;
        setLoadError(
          error instanceof Error
            ? error.message
            : 'Impossibile caricare l’analisi.'
        );
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingAnalysis(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    analysisId,
    applySnapshot,
    applyWorkspaceSettings,
    queryPersistReady,
    loadSessionsForGroup,
    queryClient,
  ]);

  const buildCurrentSnapshot =
    useCallback((): SpectralWorkspaceSnapshot | null => {
      if (!meta) return null;

      return buildSnapshotFromWorkspace({
        ...meta,
        activeSessionGroupId,
        customSegments,
        workspace: {
          activeViewId,
          trendStart,
          trendEnd,
          selectedFeature,
          trendNormalizationEnabled,
          trendLowPassEnabled,
          showOverlaySessions,
          selectedOverlaySessionIds,
          selectedSpectraSessionId,
          spectraStart,
          spectraEnd,
          spectraLogScale,
        },
      });
    }, [
      activeSessionGroupId,
      activeViewId,
      customSegments,
      meta,
      selectedFeature,
      selectedOverlaySessionIds,
      selectedSpectraSessionId,
      showOverlaySessions,
      spectraEnd,
      spectraLogScale,
      spectraStart,
      trendEnd,
      trendLowPassEnabled,
      trendNormalizationEnabled,
      trendStart,
    ]);

  useEffect(() => {
    const persistence = persistenceRef.current;

    if (!analysisId || !meta || !persistence.hydrated || persistence.skipSave) {
      return;
    }

    const snapshot = buildCurrentSnapshot();
    if (!snapshot) return;

    const fingerprint = serializeSnapshotForSave(snapshot);
    if (fingerprint === persistence.lastSavedSnapshot) {
      return;
    }

    const timer = window.setTimeout(() => {
      const latestPersistence = persistenceRef.current;
      const latestSnapshot = buildCurrentSnapshot();
      if (!latestSnapshot) return;

      const latestFingerprint = serializeSnapshotForSave(latestSnapshot);
      if (
        latestFingerprint === latestPersistence.lastSavedSnapshot ||
        latestPersistence.saveInFlight
      ) {
        return;
      }

      latestPersistence.saveInFlight = true;
      setIsSaving(true);

      spectralWorkspaceStore
        .updateAnalysis(analysisId, latestSnapshot)
        .then(updated => {
          persistenceRef.current.lastSavedSnapshot =
            serializeSnapshotForSave(updated);
          setMeta(current =>
            current
              ? {
                  ...current,
                  name: updated.name,
                  description: updated.description,
                }
              : current
          );
          if (analysisId) {
            writeCachedAnalysisSnapshot(analysisId, updated);
          }
          notifications.show({
            color: 'teal',
            title: 'Analisi salvata',
            message: 'Le modifiche al workspace sono state salvate.',
            autoClose: 2500,
          });
        })
        .catch(error => {
          notifications.show({
            color: 'red',
            title: 'Salvataggio non riuscito',
            message:
              error instanceof Error
                ? error.message
                : 'Impossibile salvare l’analisi.',
          });
        })
        .finally(() => {
          persistenceRef.current.saveInFlight = false;
          setIsSaving(false);
        });
    }, AUTOSAVE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [analysisId, buildCurrentSnapshot, meta]);

  const handleActiveSessionGroupChange = useCallback(
    async (groupId: string | null) => {
      setActiveSessionGroupId(groupId);
      setSelectedOverlaySessionIds([]);
      lastHydratedKeyRef.current = null;
      await loadSessionsForGroup(groupId, {
        force: true,
        deferEventParse: false,
      });
    },
    [loadSessionsForGroup]
  );

  const updateMeta = useCallback(
    (values: { name: string; description?: string }) => {
      setMeta(current => {
        if (!current) return current;
        return {
          ...current,
          name: values.name,
          description: values.description,
        };
      });
    },
    []
  );

  const isParsingSessionEvents = useMemo(
    () => hasPendingSessionEventParse(sessionList),
    [sessionList]
  );

  return {
    sessionList,
    readySessions,
    isLoading,
    isParsingSessionEvents,
    isLoadingSessions: isPreparingSessions || isLoading,
    ensureSessionEventsParsed,
    meta,
    updateMeta,
    activeSessionGroupId,
    handleActiveSessionGroupChange,
    isLoadingAnalysis,
    isSaving,
    loadError,
    selectedFeature,
    setSelectedFeature,
    showOverlaySessions,
    setShowOverlaySessions,
    selectedOverlaySessionIds,
    setSelectedOverlaySessionIds,
    activeViewId,
    setActiveViewId,
    customSegments,
    setCustomSegments,
    trendStart,
    setTrendStart,
    trendEnd,
    setTrendEnd,
    trendNormalizationEnabled,
    setTrendNormalizationEnabled,
    trendLowPassEnabled,
    setTrendLowPassEnabled,
    selectedSpectraSessionId,
    setSelectedSpectraSessionId,
    spectraStart,
    setSpectraStart,
    spectraEnd,
    setSpectraEnd,
    spectraLogScale,
    setSpectraLogScale,
    defaultWorkspaceSettings: createDefaultWorkspaceSettings(),
  };
};
